// pages/settings/CallSwitchSettings.jsx - COMPLETELY UPDATED
import { useEffect, useState, useCallback } from "react";
import {
  ArrowLeft,
  RefreshCw,
  Save,
  Phone,
  ShieldCheck,
  BarChart3,
  Archive,
  XCircle,
  CheckCircle,
  AlertCircle,
  Settings,
  Users,
  ChevronDown,
  ChevronUp,
  Search,
  Loader2,
  Download,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";

const PROCESSING_TYPES = {
  lead_qualification: {
    label: "Lead Qualification",
    description:
      "Full AI pipeline: transcription, sentiment, insights, qualification scoring, and script adherence",
    icon: ShieldCheck,
    color: "text-green-400",
    bg: "bg-green-600/20",
    border: "border-green-600/30",
  },
  sales_call: {
    label: "Sales Call Processing",
    description:
      "Sales-focused pipeline: transcription, insights, meeting qualification, and CRM integration",
    icon: BarChart3,
    color: "text-blue-400",
    bg: "bg-blue-600/20",
    border: "border-blue-600/30",
  },
  audio_only: {
    label: "Audio Storage Only",
    description:
      "Upload and store the recording without any AI processing or transcription",
    icon: Archive,
    color: "text-yellow-400",
    bg: "bg-yellow-600/20",
    border: "border-yellow-600/30",
  },
  disabled: {
    label: "Disabled",
    description:
      "Skip this extension entirely — recordings will not be downloaded or stored",
    icon: XCircle,
    color: "text-red-400",
    bg: "bg-red-600/20",
    border: "border-red-600/30",
  },
};

function ExtensionConfigRow({ extension, onUpdate, isSaving }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localConfig, setLocalConfig] = useState({
    processingType: extension.processingType || "lead_qualification",
    isActive: extension.isActive ?? true,
    extensionName: extension.extensionName || extension.callSwitchName || "",
    externalUserId: extension.externalUserId || "",
    externalUserName: extension.externalUserName || "",
    processingSettings: extension.processingSettings || {},
  });
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field, value) => {
    setLocalConfig((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSettingChange = (field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      processingSettings: { ...prev.processingSettings, [field]: value },
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(extension.extensionNumber, localConfig);
    setHasChanges(false);
  };

  const typeInfo = PROCESSING_TYPES[localConfig.processingType];
  const TypeIcon = typeInfo.icon;

  return (
    <div
      className={`border rounded-lg transition-all ${
        hasChanges ? "border-purple-500/50" : "border-gray-700"
      } bg-[#1a1d23]`}
    >
      {/* Row header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#22252b] transition-colors rounded-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Extension badge */}
          <div className="flex-shrink-0 w-16 h-10 bg-[#0C0E12] border border-gray-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-mono font-semibold text-sm">
              {extension.extensionNumber}
            </span>
          </div>

          {/* Name + info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium truncate">
                {localConfig.extensionName ||
                  extension.callSwitchName ||
                  "Unnamed Extension"}
              </span>
              {extension.source && (
                <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
                  {extension.source === "callswitch_api"
                    ? "CallSwitch"
                    : extension.source === "call_history"
                      ? "History"
                      : "Manual"}
                </span>
              )}
              {extension.isActive && (
                <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span
                className={`text-xs flex items-center gap-1 ${typeInfo.color}`}
              >
                <TypeIcon className="h-3 w-3" />
                {typeInfo.label}
              </span>
              {extension.totalCallsProcessed > 0 && (
                <span className="text-xs text-gray-500">
                  {extension.totalCallsProcessed} calls processed
                </span>
              )}
              {extension.lastSyncedAt && (
                <span className="text-xs text-gray-500">
                  Last synced:{" "}
                  {new Date(extension.lastSyncedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {hasChanges && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded configuration - SAME AS BEFORE */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-700/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Display Name */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={localConfig.extensionName}
                onChange={(e) => handleChange("extensionName", e.target.value)}
                placeholder="e.g. John Smith"
                className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>

            {/* Processing Type */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Processing Pipeline
              </label>
              <select
                value={localConfig.processingType}
                onChange={(e) => handleChange("processingType", e.target.value)}
                className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              >
                {Object.entries(PROCESSING_TYPES).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Processing type description */}
          <div
            className={`mt-3 p-3 rounded-lg ${typeInfo.bg} border ${typeInfo.border}`}
          >
            <div className="flex items-start gap-2">
              <TypeIcon
                className={`h-4 w-4 mt-0.5 flex-shrink-0 ${typeInfo.color}`}
              />
              <p className="text-sm text-gray-300">{typeInfo.description}</p>
            </div>
          </div>

          {/* Sales Call specific settings */}
          {localConfig.processingType === "sales_call" && (
            <div className="mt-4 p-4 bg-blue-900/10 border border-blue-800/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-300 mb-3">
                Sales Call Settings
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    External User ID (CRM)
                  </label>
                  <input
                    type="text"
                    value={localConfig.externalUserId}
                    onChange={(e) =>
                      handleChange("externalUserId", e.target.value)
                    }
                    placeholder="CRM user ID"
                    className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    External User Name (CRM)
                  </label>
                  <input
                    type="text"
                    value={localConfig.externalUserName}
                    onChange={(e) =>
                      handleChange("externalUserName", e.target.value)
                    }
                    placeholder="CRM user name"
                    className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Min Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={localConfig.processingSettings.minDuration ?? 30}
                    onChange={(e) =>
                      handleSettingChange(
                        "minDuration",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    External Account ID
                  </label>
                  <input
                    type="text"
                    value={
                      localConfig.processingSettings.externalAccountId || ""
                    }
                    onChange={(e) =>
                      handleSettingChange("externalAccountId", e.target.value)
                    }
                    placeholder="Optional"
                    className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Lead Qualification specific settings */}
          {localConfig.processingType === "lead_qualification" && (
            <div className="mt-4 p-4 bg-green-900/10 border border-green-800/20 rounded-lg">
              <h4 className="text-sm font-medium text-green-300 mb-3">
                Lead Qualification Settings
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Min Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={localConfig.processingSettings.minDuration ?? 90}
                    onChange={(e) =>
                      handleSettingChange(
                        "minDuration",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <input
                    type="checkbox"
                    id={`skip-short-${extension.extensionNumber}`}
                    checked={
                      localConfig.processingSettings.skipShortCalls !== false
                    }
                    onChange={(e) =>
                      handleSettingChange("skipShortCalls", e.target.checked)
                    }
                    className="rounded border-gray-600 bg-[#0C0E12] text-purple-500 focus:ring-purple-500"
                  />
                  <label
                    htmlFor={`skip-short-${extension.extensionNumber}`}
                    className="text-sm text-gray-300"
                  >
                    Skip short calls
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Audio Only settings */}
          {localConfig.processingType === "audio_only" && (
            <div className="mt-4 p-4 bg-yellow-900/10 border border-yellow-800/20 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-300 mb-3">
                Audio Storage Settings
              </h4>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-400" />
                <p className="text-sm text-gray-400">
                  Recordings will be uploaded to S3 and a database record
                  created, but no AI processing will be performed. You can
                  always re-process later.
                </p>
              </div>
            </div>
          )}

          {/* Active toggle */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localConfig.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
              <span className="text-sm text-gray-300">Active</span>
            </div>

            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Changes
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CallSwitchSettings() {
  const navigate = useNavigate();
  const [extensions, setExtensions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [syncStatus, setSyncStatus] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
  });

  const fetchExtensions = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.getCallSwitchExtensions();
      setExtensions(response.extensions || []);
      setStats({
        total: response.extensions?.length || 0,
        active: response.extensions?.filter((e) => e.isActive).length || 0,
      });
    } catch (error) {
      console.error("Failed to fetch extensions:", error);
      toast.error("Failed to load CallSwitch extensions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const status = await api.getCallSwitchSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error("Failed to fetch sync status:", error);
    }
  }, []);

  useEffect(() => {
    fetchExtensions();
    fetchSyncStatus();
  }, [fetchExtensions, fetchSyncStatus]);

  const handleDiscoverExtensions = async () => {
    try {
      setIsDiscovering(true);
      const result = await api.discoverCallSwitchExtensions();

      toast.success(
        `Discovered ${result.discovered} extensions (${result.created} new, ${result.updated} updated)`,
      );

      await fetchExtensions();
    } catch (error) {
      console.error("Failed to discover extensions:", error);
      toast.error("Failed to discover extensions from CallSwitch");
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleUpdateConfig = async (extensionNumber, configData) => {
    try {
      setIsSaving(true);
      await api.updateCallSwitchExtensionConfig(extensionNumber, configData);
      toast.success(`Configuration saved for extension ${extensionNumber}`);
      await fetchExtensions();
    } catch (error) {
      console.error("Failed to save config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncRecordings = async () => {
    try {
      setIsSyncing(true);
      const result = await api.syncCallSwitchRecordings();
      toast.success(
        `Sync started: ${result.synced || 0} recordings synced, ${result.failed || 0} failed`,
      );
      await fetchSyncStatus();
    } catch (error) {
      console.error("Failed to sync:", error);
      toast.error("Failed to start sync");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSetAllToType = async (processingType) => {
    const confirm = window.confirm(
      `Set all ${filteredExtensions.length} visible extensions to "${PROCESSING_TYPES[processingType].label}"?`,
    );
    if (!confirm) return;

    try {
      setIsSaving(true);
      const configs = filteredExtensions.map((ext) => ({
        extensionNumber: ext.extensionNumber,
        processingType,
        extensionName: ext.extensionName || ext.callSwitchName || "",
        isActive: true,
      }));

      await api.bulkUpdateCallSwitchConfigs(configs);
      toast.success(
        `Updated ${configs.length} extensions to ${PROCESSING_TYPES[processingType].label}`,
      );
      await fetchExtensions();
    } catch (error) {
      console.error("Bulk update failed:", error);
      toast.error("Failed to bulk update configurations");
    } finally {
      setIsSaving(false);
    }
  };

  // Filter extensions
  const filteredExtensions = extensions.filter((ext) => {
    const matchesSearch =
      !searchTerm ||
      ext.extensionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ext.callSwitchName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (ext.extensionName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterType === "all" ||
      (filterType === "active" && ext.isActive) ||
      (filterType === "inactive" && !ext.isActive) ||
      ext.processingType === filterType;

    return matchesSearch && matchesFilter;
  });

  // Summary counts
  const typeCounts = extensions.reduce((acc, ext) => {
    const type = ext.processingType || "unconfigured";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading CallSwitch extensions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/settings?tab=integrations")}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Integrations
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <Phone className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">
                  CallSwitch Extension Configuration
                </h1>
                <p className="text-gray-400 text-sm mt-1">
                  Configure processing pipelines for each extension
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDiscoverExtensions}
                disabled={isDiscovering}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isDiscovering
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isDiscovering ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Discover
              </button>
              <button
                onClick={fetchExtensions}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Sync Status Banner */}
        {syncStatus && (
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-purple-400" />
                <div>
                  <p className="text-sm font-medium text-white">
                    Automatic Sync Active
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Next sync in {syncStatus.nextSyncMinutes || "15"} minutes •
                    Last synced:{" "}
                    {syncStatus.lastSyncedAt
                      ? new Date(syncStatus.lastSyncedAt).toLocaleString()
                      : "Never"}
                  </p>
                </div>
              </div>
              <button
                onClick={handleSyncRecordings}
                disabled={isSyncing}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                  isSyncing
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 text-white"
                }`}
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Sync Now
              </button>
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-gray-400 mt-1">Total</p>
          </div>
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-purple-400">{stats.active}</p>
            <p className="text-xs text-gray-400 mt-1">Active</p>
          </div>
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-400">
              {typeCounts.lead_qualification || 0}
            </p>
            <p className="text-xs text-gray-400 mt-1">Lead Qual</p>
          </div>
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-400">
              {typeCounts.sales_call || 0}
            </p>
            <p className="text-xs text-gray-400 mt-1">Sales</p>
          </div>
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">
              {typeCounts.audio_only || 0}
            </p>
            <p className="text-xs text-gray-400 mt-1">Audio Only</p>
          </div>
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-400">
              {typeCounts.disabled || 0}
            </p>
            <p className="text-xs text-gray-400 mt-1">Disabled</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search extensions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg pl-10 pr-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>

            {/* Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="all">All Extensions</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="lead_qualification">Lead Qualification</option>
              <option value="sales_call">Sales Call</option>
              <option value="audio_only">Audio Only</option>
              <option value="disabled">Disabled</option>
            </select>

            {/* Bulk actions */}
            <div className="relative group">
              <button className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors whitespace-nowrap">
                <Settings className="h-4 w-4" />
                Bulk Set
                <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute right-0 top-full mt-1 w-56 bg-[#22252b] border border-gray-600 rounded-lg shadow-xl z-10 hidden group-hover:block">
                {Object.entries(PROCESSING_TYPES).map(([key, info]) => {
                  const Icon = info.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => handleSetAllToType(key)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors first:rounded-t-lg last:rounded-b-lg"
                    >
                      <Icon className={`h-4 w-4 ${info.color}`} />
                      {info.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* No extensions state */}
        {extensions.length === 0 && (
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-8 text-center">
            <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">
              No extensions found. Click "Discover" to fetch extensions from
              CallSwitch.
            </p>
            <button
              onClick={handleDiscoverExtensions}
              disabled={isDiscovering}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg mx-auto transition-colors disabled:opacity-50"
            >
              {isDiscovering ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Discover Extensions
            </button>
          </div>
        )}

        {/* Extension list */}
        {extensions.length > 0 && (
          <div className="space-y-2">
            {filteredExtensions.length === 0 ? (
              <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-8 text-center">
                <Users className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">
                  No extensions match your filters
                </p>
              </div>
            ) : (
              filteredExtensions.map((ext) => (
                <ExtensionConfigRow
                  key={ext.extensionNumber}
                  extension={ext}
                  onUpdate={handleUpdateConfig}
                  isSaving={isSaving}
                />
              ))
            )}
          </div>
        )}

        {/* Footer info */}
        {extensions.length > 0 && (
          <div className="mt-6 text-xs text-gray-500 text-center">
            Showing {filteredExtensions.length} of {extensions.length}{" "}
            extensions • Changes are saved immediately • Automatic sync runs
            every 15 minutes
          </div>
        )}
      </div>
    </div>
  );
}

export default CallSwitchSettings;
